import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import emptyFunction from 'react-utils/emptyFunction';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import UITextToolbarIcon from 'rich-text-lib/components/editor/UITextToolbarIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import KnowledgePopoverEmptyState from './draft-content-transplant-ka/KnowledgePopoverEmptyState';
import KnowledgePopoverErrorState from './draft-content-transplant-ka/KnowledgePopoverErrorState';
import transformArticlesData from './draft-content-transplant-ka/transformArticlesData';
import * as KnowledgeArticlesApi from './draft-content-transplant-ka/KnowledgeArticlesApi';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { insertLink } from '../../utils/documentUtils';
import KBArticlesTypeahead from './KBArticlesTypeahead';
import { getKnowledgeUrl } from '../../utils/manageLinks';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import { getGlobalVars } from '../../utils/configUtils';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import MultiKBs from './MultiKBs';
import { AuthContext } from '../../contexts/AuthContext';
import { jsx as _jsx } from "react/jsx-runtime";
const KnowledgeArticleButton = ({
  onClick
}) => /*#__PURE__*/_jsx(UITooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertKnowledgeArticle"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "knowledge-articles",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(UITextToolbarIcon, {
      name: "cap"
    })
  })
});
export default function InsertKnowledgeArticle({
  useContentContainer,
  onViewInsertMenu = emptyFunction,
  onInsertComplete = emptyFunction
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const editorConfig = useContext(EditorConfigContext);
  const {
    apiClient = hubHttpClient,
    onInsertContent = identity,
    onOpenPopover = identity,
    onSearchAllKBs,
    onSelectKB,
    outputType = contentOutputs.RENDERED,
    useMultipleKBs = false
  } = editorConfig.KNOWLEDGE_ARTICLE || {};
  const auth = useContext(AuthContext);
  const useV3 = Boolean(auth && auth.gates.includes('KnowledgeBase:V3.1'));
  const {
    usePlaintextMode,
    plaintextOptions
  } = useMemo(() => getGlobalVars(editorConfig), [editorConfig]);
  const insertArticle = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const {
    apiFunctions,
    insertKnowledgeArticlesDecoratorConfig
  } = useMemo(() => {
    const api = KnowledgeArticlesApi.defaultApiWrapper(apiClient, useV3 ? '3' : '2');
    return {
      apiFunctions: api,
      insertKnowledgeArticlesDecoratorConfig: {
        outputType,
        apiFunctions: api,
        mergeTagOptions: {
          includeCustomTokens: true
        }
      }
    };
  }, [apiClient, outputType, useV3]);
  const KnowledgeArticlesPopover = useMemo(() => {
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button: KnowledgeArticleButton,
      PopoverErrorState: KnowledgePopoverErrorState,
      PopoverEmptyState: KnowledgePopoverEmptyState,
      onOpenPopover,
      Header: GenericPopoverHeader,
      popoverClassName: 'prosemirror-knowledge-articles-popover',
      searchPlaceHolder: 'draftPlugins.knowledgeArticlesPlugin.popover.search.placeholder',
      transformData: transformArticlesData,
      search: apiFunctions.searchArticles,
      popoverTestKey: 'knowledge-articles-popover',
      // @ts-expect-error portalId can be undefined in unit testing for example
      manageUrl: getKnowledgeUrl(PortalIdParser.get()),
      titleMessage: 'draftPlugins.knowledgeArticlesPlugin.popover.header.insertKnowledgeArticle'
    }), insertKnowledgeArticlesDecoratorConfig);
  }, [insertKnowledgeArticlesDecoratorConfig, onInsertContent, onOpenPopover, apiFunctions.searchArticles]);
  if (useMultipleKBs) {
    return /*#__PURE__*/_jsx(MultiKBs, {
      onClickBack: onViewInsertMenu,
      onSelectArticle: insertArticle,
      onSearchAllKBs: onSearchAllKBs,
      onSelectKB: onSelectKB,
      portalId: PortalIdParser.get()
    });
  }
  return useContentContainer ? /*#__PURE__*/_jsx(KBArticlesTypeahead, {
    apiFunctions: apiFunctions,
    onChange: insertArticle,
    onClickBack: onViewInsertMenu,
    portalId: PortalIdParser.get()
  }) : /*#__PURE__*/_jsx(KnowledgeArticlesPopover, {
    onChange: insertArticle,
    useProsemirror: true
  });
}